'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

const LoginForm = () => {
  const router = useRouter();
  const { signIn, user, loading: authLoading, isAdmin } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 🔥 NORMAL USER AUTO-REDIRECT
  useEffect(() => {
    if (!authLoading && user && !isAdmin) {
      //console.log(' Normal User → Product Catalog');
      router.push('/product-catalog');
    }
  }, [authLoading, user, isAdmin, router]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    //console.log('🔐 Normal User Login:', formData.email);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);

      if (error) {
       // console.error(' Login Error:', error);
        setErrors({
          submit: error?.message || 'Failed to sign in. Please check your credentials.'
        });
        setIsLoading(false);
        return;
      }

      // Handle remember me functionality
      if (formData?.rememberMe) {
        try {
          localStorage.setItem('rememberedEmail', formData?.email);
        } catch (error) {
          //console.error('Failed to save email:', error);
        }
      } else {
        try {
          localStorage.removeItem('rememberedEmail');
        } catch (error) {
          console.error('Failed to remove saved email:', error);
        }
      }

      //  REDIRECT HANDLED BY useEffect (Normal user → /product-catalog)
      //console.log(' Login Success - Redirecting...');

    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        submit: 'An unexpected error occurred. Please try again.'
      });
      setIsLoading(false);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6" noValidate>
      {/* Email Field */}
      <div className="space-y-2">
        <label 
          htmlFor="email" 
          className="block text-sm font-medium text-foreground caption"
        >
          Email Address <span className="text-error">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Icon 
              name="EnvelopeIcon" 
              size={20} 
              className="text-muted-foreground"
            />
          </div>
          <input
            type="email"
            id="email"
            name="email"
            value={formData?.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            className={`w-full h-12 pl-12 pr-4 bg-card border rounded-md text-sm transition-smooth focus:outline-none focus:ring-3 focus:ring-offset-3 ${
              errors?.email
                ? 'border-error focus:ring-error' :'border-border focus:ring-ring'
            }`}
            disabled={isLoading}
            aria-invalid={errors?.email ? 'true' : 'false'}
            aria-describedby={errors?.email ? 'email-error' : undefined}
          />
        </div>
        {errors?.email && (
          <p id="email-error" className="text-sm text-error caption flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={16} />
            {errors?.email}
          </p>
        )}
      </div>

      {/* Password Field */}
      <div className="space-y-2">
        <label 
          htmlFor="password" 
          className="block text-sm font-medium text-foreground caption"
        >
          Password <span className="text-error">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Icon 
              name="LockClosedIcon" 
              size={20} 
              className="text-muted-foreground"
            />
          </div>
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData?.password}
            onChange={handleChange}
            placeholder="Enter your password"
            className={`w-full h-12 pl-12 pr-12 bg-card border rounded-md text-sm transition-smooth focus:outline-none focus:ring-3 focus:ring-offset-3 ${
              errors?.password
                ? 'border-error focus:ring-error' :'border-border focus:ring-ring'
            }`}
            disabled={isLoading}
            aria-invalid={errors?.password ? 'true' : 'false'}
            aria-describedby={errors?.password ? 'password-error' : undefined}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-foreground transition-smooth"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            disabled={isLoading}
          >
            <Icon 
              name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} 
              size={20}
            />
          </button>
        </div>
        {errors?.password && (
          <p id="password-error" className="text-sm text-error caption flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={16} />
            {errors?.password}
          </p>
        )}
      </div>

      {/* Remember Me & Forgot Password */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="rememberMe"
            checked={formData?.rememberMe}
            onChange={handleChange}
            className="w-4 h-4 rounded border-border text-primary focus:ring-3 focus:ring-ring focus:ring-offset-3 transition-smooth"
            disabled={isLoading}
          />
          <span className="text-sm text-foreground caption">Remember me</span>
        </label>
        <Link
          href="/forgot-password"
          className="text-sm font-medium text-primary hover:text-primary/80 transition-smooth caption focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-1 -mx-1"
        >
          Forgot Password?
        </Link>
      </div>

      {/* Submit Error */}
      {errors?.submit && (
        <div className="p-4 bg-error/10 border border-error rounded-md">
          <p className="text-sm text-error caption flex items-start gap-2">
            <Icon name="ExclamationTriangleIcon" size={20} className="flex-shrink-0 mt-0.5" />
            <span>{errors?.submit}</span>
          </p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || authLoading}
        className="w-full h-12 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isLoading || authLoading ? (
          <>
            <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
            <span>Signing In...</span>
          </>
        ) : (
          <>
            <Icon name="ArrowRightOnRectangleIcon" size={20} />
            <span>Sign In</span>
          </>
        )}
      </button>

      {/* Register Link */}
      <div className="text-center pt-4 border-t border-border">
        <p className="text-sm text-muted-foreground caption">
          Don&apos;t have an account?{' '}
          <Link
            href="/registration"
            className="font-medium text-primary hover:text-primary/80 transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-1 -mx-1"
          >
            Create New Account
          </Link>
        </p>
        {/*  ADMIN LINK */}
        <p className="text-xs text-muted-foreground mt-2">
          Admin? <Link href="/admin-login" className="text-primary font-medium hover:underline">Login here →</Link>
        </p>
      </div>
    </form>
  );
};

LoginForm.propTypes = {};

export default LoginForm;
